import { FormikProps } from 'formik';

import { IAbstractRecord } from '@models';

export const INVALID_DATE_MESSAGE = 'Invalid date';

/** Internal sentinel used when invalid pickers cannot be mapped to a field name. */
export const INVALID_DATE_PICKER_FORM_ERROR_KEY = '__invalidDatePickers';

const invalidDatePickerInstances = new Set<string>();
const invalidDatePickerFieldNames = new Set<string>();
const patchedFormikInstances = new WeakSet<object>();

export function setDatePickerFieldInvalid(
  instanceId: string,
  fieldName: string | undefined,
  isInvalid: boolean,
  registerFieldName = true
) {
  if (!instanceId) return;

  if (isInvalid) {
    invalidDatePickerInstances.add(instanceId);
    if (registerFieldName && fieldName) {
      invalidDatePickerFieldNames.add(fieldName);
    }
  } else {
    invalidDatePickerInstances.delete(instanceId);
    if (registerFieldName && fieldName) {
      invalidDatePickerFieldNames.delete(fieldName);
    }
  }
}

export function clearDatePickerFieldInvalid(
  instanceId: string,
  fieldName: string | undefined,
  registerFieldName = true
) {
  if (!instanceId) return;

  invalidDatePickerInstances.delete(instanceId);
  if (registerFieldName && fieldName) {
    invalidDatePickerFieldNames.delete(fieldName);
  }
}

export function clearAllInvalidDatePickerFields() {
  invalidDatePickerInstances.clear();
  invalidDatePickerFieldNames.clear();
}

export function hasInvalidDatePickerFields(): boolean {
  return invalidDatePickerInstances.size > 0;
}

export function getInvalidDatePickerFieldNames(): string[] {
  return Array.from(invalidDatePickerFieldNames);
}

export function mergeInvalidDatePickerErrors(
  errors: IAbstractRecord
): IAbstractRecord {
  const mergedErrors = { ...errors };

  Object.keys(mergedErrors).forEach((fieldName) => {
    if (
      mergedErrors[fieldName] === INVALID_DATE_MESSAGE &&
      !invalidDatePickerFieldNames.has(fieldName)
    ) {
      mergedErrors[fieldName] = '';
    }
  });

  invalidDatePickerFieldNames.forEach((fieldName) => {
    mergedErrors[fieldName] = INVALID_DATE_MESSAGE;
  });

  return mergedErrors;
}

export function applyInvalidDatePickerErrors(
  formik?: Pick<FormikProps<unknown>, 'setFieldError' | 'setFieldTouched'> | null
): boolean {
  if (!hasInvalidDatePickerFields()) {
    return false;
  }

  invalidDatePickerFieldNames.forEach((fieldName) => {
    formik?.setFieldError(fieldName, INVALID_DATE_MESSAGE);
    formik?.setFieldTouched(fieldName, true, false);
  });

  return true;
}

export function hasNestedFormErrors(err: unknown): boolean {
  if (!err) return false;
  if (typeof err === 'string') return err.length > 0;
  if (Array.isArray(err)) return err.some(hasNestedFormErrors);
  if (typeof err === 'object') {
    return Object.values(err as Record<string, unknown>).some(
      hasNestedFormErrors
    );
  }

  return false;
}

function mergeValidationResultWithDatePickers(
  errors: IAbstractRecord,
  formik?: Pick<FormikProps<unknown>, 'setFieldError' | 'setFieldTouched'> | null
): IAbstractRecord {
  applyInvalidDatePickerErrors(formik);
  const mergedErrors = mergeInvalidDatePickerErrors(errors);

  if (
    hasInvalidDatePickerFields() &&
    !hasNestedFormErrors(mergedErrors)
  ) {
    return {
      ...mergedErrors,
      [INVALID_DATE_PICKER_FORM_ERROR_KEY]: INVALID_DATE_MESSAGE
    };
  }

  return mergedErrors;
}

/**
 * Patches Formik's validateForm once so every existing page action that calls
 * validateForm() automatically includes invalid date picker state.
 */
export function ensureFormikValidateFormIncludesDatePickers(
  formik?: FormikProps<unknown> | null
) {
  if (!formik || patchedFormikInstances.has(formik)) {
    return;
  }

  patchedFormikInstances.add(formik);

  const originalValidateForm = formik.validateForm.bind(formik);

  formik.validateForm = async (values) => {
    const schemaErrors = ((await originalValidateForm(values)) ||
      {}) as IAbstractRecord;

    return mergeValidationResultWithDatePickers(schemaErrors, formik);
  };
}

export function includeDatePickerValidationInFormResult<
  TResult extends { isValid: boolean }
>(result: TResult): TResult {
  if (!hasInvalidDatePickerFields()) {
    return result;
  }

  return { ...result, isValid: false };
}
